import { Currency } from "@mcleod/core";
import {
    Button,
    DataSource,
    DataSourceAction,
    DataSourceExecutionEvent,
    Tab, TableRow, TableSelectionEvent} from "@mcleod/components";
import { AutogenLayoutWireFundsProcessing } from "./autogen/AutogenLayoutWireFundsProcessing";
import { ProcessingHandler, Action, ProcessingStepper } from "@mcleod/integrations";
import { ProcessLogViewer } from "@mcleod/general";

const CHECK_AMOUNT_FIELD = "check_amount";

export class WireFundsProcessing extends AutogenLayoutWireFundsProcessing {
    private processingHandler: ProcessingHandler;

    onLoad(): void | Promise<void> {
        this.processingHandler = new ProcessingHandler({
            endpoint: "lme/dispatch/asset/post-wires",
            title: "Wire Transaction"
        }, this.layoutReadyWireFundsTable.tableWireFundsProcessing);
        this.processingHandler.buttonPostOnClick = (action: Action) => this.processingActionOnPost(action);
        this.processingHandler.multiButton.primaryButton.disabledTooltip = "Select wire(s) to enable posting options";

        this.layoutReadyWireFundsTable.tableWireFundsProcessing.addSelectionListener(async (event: TableSelectionEvent) => {
            this.processingHandler.multiButton.enabled = event.newRows.length > 0;
        });

        const resolver = () => {
            this.reloadAllTabs();
            return null;
        };

        this.layoutHoldWireFundsTable.tableWireFundsProcessing.doOnResolveRowEdit = resolver;
        this.layoutUnverifiedWireFundsTable.tableWireFundsProcessing.doOnResolveRowEdit = resolver;
        this.layoutReadyWireFundsTable.tableWireFundsProcessing.doOnResolveRowEdit = resolver;
        this.layoutVoidWireFundsTable.tableWireFundsProcessing.doOnResolveRowEdit = resolver;

        this.layoutHoldWireFundsTable.layoutWireFundsProcessingToolPanel.searchCallback = resolver;
        this.layoutUnverifiedWireFundsTable.layoutWireFundsProcessingToolPanel.searchCallback = resolver;
        this.layoutReadyWireFundsTable.layoutWireFundsProcessingToolPanel.searchCallback = resolver;
        this.layoutVoidWireFundsTable.layoutWireFundsProcessingToolPanel.searchCallback = resolver;

        this.layoutHoldWireFundsTable.mainDataSource.addAfterExecutionListener((event) => this.doAfterExecute(event, this.tabHold));
        this.layoutUnverifiedWireFundsTable.mainDataSource.addAfterExecutionListener((event) => this.doAfterExecute(event, this.tabUnverified));
        this.layoutReadyWireFundsTable.mainDataSource.addAfterExecutionListener((event) => this.doAfterExecute(event, this.tabReady));
        this.layoutVoidWireFundsTable.mainDataSource.addAfterExecutionListener((event) => this.doAfterExecute(event, this.tabVoid));

        this.reloadAllTabs();
        this.setupReadyTab();
    }

    private reloadAllTabs() {
        this.layoutHoldWireFundsTable.mainDataSource.search();
        this.layoutUnverifiedWireFundsTable.mainDataSource.search();
        this.layoutReadyWireFundsTable.mainDataSource.search();
        this.layoutVoidWireFundsTable.mainDataSource.search();
    }

    setupReadyTab() {
        this.tabReady.select();
        const processLogButton = new Button({
            caption: "Process Log Viewer",
            color: "primary",
            onClick: () => ProcessLogViewer.showInSlideoutDecorator("Wire Funds Posting")
        });
        this.layoutReadyWireFundsTable.tableWireFundsProcessing.addRightTool(processLogButton);
    }

    doAfterExecute(event: DataSourceExecutionEvent, tab: Tab) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            if (tab == this.tabUnverified) {
                tab.visible = this.layoutUnverifiedWireFundsTable.mainDataSource.rowCount > 0;
            } else if (tab == this.tabVoid) {
                tab.visible = this.layoutVoidWireFundsTable.mainDataSource.rowCount > 0;
            }
            if (!tab.visible && tab.selected) {
                this.tabReady.select();
            }
            this.updateTabTitle(tab, event.dataSource);
        }
    }

    updateTabTitle(tab: Tab, dataSource: DataSource) {
        if (tab.caption.indexOf("(") > -1) {
            tab.caption = tab.caption.substring(0, tab.caption.indexOf("("));
        }
        tab.caption = `${tab.caption} (${dataSource.rowCount}) ${dataSource?.summaryData?.getCurrency("total_check_sum")}`;
    }

    /**
     * Calculates the sum of check amounts for the selected rows
     */
    async calculatePostableTotals() {
        const sum: Currency = new Currency(0);
        const selectedRows = this.layoutReadyWireFundsTable.tableWireFundsProcessing.selectedRows;
        for (const row of selectedRows) {
            const checkAmount = row.data.getCurrency(CHECK_AMOUNT_FIELD);
            if (checkAmount) {
                await sum.add(checkAmount);
            }
        }

        return { count: selectedRows.length, amount: sum };
    }

    processingActionOnPost(action: Action) {
        const selectedWireIds = this.getSelectedWireIds();
        this.processingHandler.showSlideout({
            title: `Process Wires (${selectedWireIds?.length}) Records`,
            doAfterSlideIn: async (decorator) => {
                const stepper = decorator.layout as ProcessingStepper;
                stepper.calculatePostableTotals = this.calculatePostableTotals.bind(this);
                stepper.doAfterPost = () => this.reloadAllTabs();
                stepper.getApiPayload = (posting: boolean) => ({
                    unposted_wire_ids: selectedWireIds,
                    print_edit_list: !posting,
                    is_posting: posting
                });
                switch (action) {
                    case Action.POST:
                        stepper.continue();
                        break;
                    case Action.PRINT_AND_POST:
                        stepper.displayReport();
                        break;
                }
            }
        });
    }

    getSelectedRows(): TableRow[] {
        return this.layoutReadyWireFundsTable.tableWireFundsProcessing.selectedRows;
    }

    public getSelectedWireIds(): string[] {
        return this.getSelectedRows().map(row => row.data.get("id") as string);
    }

}
